import React, { useState, useEffect } from "react";
import {
	CraftModal,
	CraftModalHeader,
	CraftModalBody,
	CraftModalFooter,
	CraftButton,
	CraftSpinner,
} from "@craft-design-system/core-components-react";
import { API_BASE_URL, getAuthHeaders } from "../../services/api";

// Must stay in sync with VALID_PAGES in the backend
const PAGE_OPTIONS = [
	{ slug: "landing", label: "Landing Page" },
	{ slug: "supply-type-allocation", label: "Supply Type Allocation" },
	{ slug: "vff", label: "Vehicle Tracking (VFF)" },
	{ slug: "series-tracking", label: "Series Tracking" },
	{ slug: "series-tracking-phase-2", label: "Series Tracking Phase 2" },
	{ slug: "dashboard", label: "Dashboard" },
];

const NotificationBannerAdmin = ({ open, onClose }) => {
	const [enabled, setEnabled] = useState(false);
	const [message, setMessage] = useState("");
	const [type, setType] = useState("warning");
	const [pages, setPages] = useState([]); // [] = all pages
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [meta, setMeta] = useState({ updated_by: "", updated_at: "" });

	useEffect(() => {
		if (open) {
			fetchBanner();
			setError("");
			setSuccess("");
		}
	}, [open]);

	const fetchBanner = async () => {
		setLoading(true);
		try {
			const res = await fetch(`${API_BASE_URL}/notification-banner`, {
				headers: getAuthHeaders(),
			});
			if (res.ok) {
				const data = await res.json();
				setEnabled(data.enabled || false);
				setMessage(data.message || "");
				setType(data.type || "warning");
				setPages(Array.isArray(data.pages) ? data.pages : []);
				setMeta({
					updated_by: data.updated_by || "",
					updated_at: data.updated_at || "",
				});
			}
		} catch (err) {
			setError("Failed to load banner settings");
		} finally {
			setLoading(false);
		}
	};

	const handleSave = async () => {
		setSaving(true);
		setError("");
		setSuccess("");
		try {
			const res = await fetch(`${API_BASE_URL}/notification-banner`, {
				method: "PUT",
				headers: getAuthHeaders(),
				body: JSON.stringify({ enabled, message, type, pages }),
			});
			if (res.ok) {
				const data = await res.json();
				setMeta({ updated_by: data.updated_by, updated_at: data.updated_at });
				setSuccess(
					pages.length === 0
						? "Banner updated successfully! Will appear on all pages."
						: `Banner updated successfully! Will appear on: ${pages
								.map((s) => PAGE_OPTIONS.find((p) => p.slug === s)?.label ?? s)
								.join(", ")}.`,
				);
			} else {
				const err = await res.json();
				setError(err.detail || "Failed to update banner");
			}
		} catch (err) {
			setError("Failed to update banner");
		} finally {
			setSaving(false);
		}
	};

	const togglePage = (slug) => {
		setPages((prev) =>
			prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
		);
	};

	const s = {
		// Constrained shell: body scrolls internally so footer stays visible.
		shell: {
			width: "660px",
			maxWidth: "100%",
			display: "flex",
			flexDirection: "column",
			maxHeight: "60vh",
		},
		scrollArea: {
			flex: 1,
			minHeight: 0,
			overflowY: "auto",
			display: "flex",
			flexDirection: "column",
			gap: "18px",
			paddingRight: "2px",
		},
		alert: {
			padding: "10px 14px",
			borderRadius: "6px",
			fontSize: "13px",
			backgroundColor: "#fde8e8",
			border: "1px solid #f5c6c6",
			color: "#cc0000",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		successAlert: {
			padding: "10px 14px",
			borderRadius: "6px",
			fontSize: "13px",
			backgroundColor: "#e8f5e9",
			border: "1px solid #b7dfb9",
			color: "#2e7d32",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		label: {
			fontSize: "12px",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
			color: "#555",
			marginBottom: "6px",
			display: "block",
		},
		hint: {
			fontSize: "12px",
			color: "#888",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			marginBottom: "8px",
			display: "block",
		},
		enableRow: {
			display: "flex",
			alignItems: "center",
			gap: "10px",
		},
		chip: (on) => ({
			display: "inline-block",
			backgroundColor: on ? "#fff4e6" : "#f2f2f2",
			border: `1px solid ${on ? "#ffd699" : "#ddd"}`,
			color: on ? "#b26a00" : "#777",
			borderRadius: "12px",
			padding: "1px 10px",
			fontSize: "11px",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
		}),
		textarea: {
			width: "100%",
			boxSizing: "border-box",
			padding: "8px 10px",
			border: "1px solid #ccc",
			borderRadius: "6px",
			fontSize: "13px",
			resize: "vertical",
			minHeight: "85px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		select: {
			width: "100%",
			boxSizing: "border-box",
			padding: "8px 10px",
			border: "1px solid #ccc",
			borderRadius: "6px",
			fontSize: "13px",
			backgroundColor: "white",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		shortcutRow: {
			display: "flex",
			gap: "8px",
			marginBottom: "10px",
		},
		shortcutBtn: (disabled) => ({
			background: "none",
			border: "1px solid #ddd",
			borderRadius: "4px",
			padding: "4px 10px",
			cursor: disabled ? "not-allowed" : "pointer",
			fontSize: "12px",
			color: disabled ? "#bbb" : "#555",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		}),
		pageList: {
			border: "1px solid #e5e7eb",
			borderRadius: "8px",
			overflow: "hidden",
		},
		pageRow: (checked, last) => ({
			display: "flex",
			alignItems: "center",
			gap: "10px",
			padding: "8px 12px",
			borderBottom: last ? "none" : "1px solid #f0f0f0",
			backgroundColor: checked ? "#f0f8ff" : "white",
			cursor: "pointer",
			fontSize: "13px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			color: "#333",
		}),
		meta: {
			fontSize: "12px",
			color: "#888",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		loadingWrap: {
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			padding: "40px",
			gap: "12px",
		},
	};

	return (
		<CraftModal show={open} allowClickOut={false} onCraftClose={onClose}>
			<CraftModalHeader
				header="Manage Notification Banner"
				content="Configure the site-wide banner message, type and target pages."
			/>
			<CraftModalBody>
				{loading ? (
					<div style={s.loadingWrap}>
						<CraftSpinner size="m" />
						<span style={{ color: "#666" }}>Loading banner settings…</span>
					</div>
				) : (
					<div style={s.shell}>
						<div style={s.scrollArea}>
							{error && <div style={s.alert}>⚠️ {error}</div>}
							{success && <div style={s.successAlert}>✓ {success}</div>}

							{/* Enable toggle */}
							<div>
								<label style={s.enableRow}>
									<input
										type="checkbox"
										checked={enabled}
										onChange={(e) => setEnabled(e.target.checked)}
									/>
									<span
										style={{
											fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
											fontSize: "13px",
											color: "#1e2832",
										}}
									>
										Enable Banner
									</span>
									<span style={s.chip(enabled)}>{enabled ? "ON" : "OFF"}</span>
								</label>
							</div>

							{/* Message */}
							<div>
								<label style={s.label}>Banner Message</label>
								<textarea
									style={s.textarea}
									value={message}
									onChange={(e) => setMessage(e.target.value)}
									placeholder="e.g. Data displayed is accurate as of 09.04.2026. New data is not yet available."
								/>
							</div>

							{/* Type */}
							<div>
								<label style={s.label}>Banner Type</label>
								<select
									style={s.select}
									value={type}
									onChange={(e) => setType(e.target.value)}
								>
									<option value="warning">Warning</option>
									<option value="error">Error</option>
								</select>
							</div>

							{/* Page targeting */}
							<div>
								<label style={s.label}>Show on pages</label>
								<span style={s.hint}>
									Leave empty to show on <strong>all pages</strong>.
								</span>

								<div style={s.shortcutRow}>
									<button
										type="button"
										style={s.shortcutBtn(pages.length === PAGE_OPTIONS.length)}
										disabled={pages.length === PAGE_OPTIONS.length}
										onClick={() => setPages(PAGE_OPTIONS.map((p) => p.slug))}
									>
										Select all
									</button>
									<button
										type="button"
										style={s.shortcutBtn(pages.length === 0)}
										disabled={pages.length === 0}
										onClick={() => setPages([])}
									>
										Clear (all pages)
									</button>
								</div>

								<div style={s.pageList}>
									{PAGE_OPTIONS.map(({ slug, label }, index) => {
										const checked = pages.includes(slug);
										return (
											<label
												key={slug}
												style={s.pageRow(
													checked,
													index === PAGE_OPTIONS.length - 1,
												)}
											>
												<input
													type="checkbox"
													checked={checked}
													onChange={() => togglePage(slug)}
												/>
												{label}
											</label>
										);
									})}
								</div>
							</div>

							{meta.updated_by && (
								<span style={s.meta}>
									Last updated by <strong>{meta.updated_by}</strong> on{" "}
									{new Date(meta.updated_at).toLocaleString()}
								</span>
							)}
						</div>
					</div>
				)}
			</CraftModalBody>
			<CraftModalFooter>
				<CraftButton variant="plain" size="m" onClick={onClose}>
					Cancel
				</CraftButton>
				<CraftButton
					variant="primary"
					size="m"
					onClick={handleSave}
					disabled={saving}
				>
					{saving ? "Saving…" : "Save & Publish"}
				</CraftButton>
			</CraftModalFooter>
		</CraftModal>
	);
};

export default NotificationBannerAdmin;
